import { useCallback, useEffect, useState } from "react";
import {
    Activity,
    RefreshCw,
    CheckCircle2,
    AlertTriangle,
    CircleDashed,
    Zap,
    Gauge,
    Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { apiErrorText } from "@/lib/errorText";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/layout/PageHeader";
import CheckinRecomputeCard from "@/components/admin/CheckinRecomputeCard";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { OPS } from "@/constants/testIds";
import { toast } from "sonner";

const S = OPS.system;

const fmtWhen = (iso, lang) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit", second: "2-digit",
        });
    } catch (_e) {
        return iso;
    }
};

function IngestStatusBadge({ status, t }) {
    if (status === "ok") {
        return (
            <Badge variant="success" className="gap-1">
                <CheckCircle2 className="h-3 w-3" />
                {t("sys_status_ok")}
            </Badge>
        );
    }
    if (status === "failed") {
        return (
            <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" />
                {t("sys_status_failed")}
            </Badge>
        );
    }
    return (
        <Badge variant="secondary" className="gap-1">
            <CircleDashed className="h-3 w-3" />
            {t("sys_status_never")}
        </Badge>
    );
}

export default function IngestStatusPage() {
    const { t, lang, tn } = useI18n();
    const { isSuperAdmin } = useAuth();
    const [sources, setSources] = useState(null);
    const [events, setEvents] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [busy, setBusy] = useState(false);
    const [emitting, setEmitting] = useState(false);
    const [confirmEmit, setConfirmEmit] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const [s, e, m] = await Promise.all([
                api.get("/system/ingest-status"),
                api.get("/system/events?limit=25"),
                api.get("/system/metrics-catalog"),
            ]);
            setSources(s.data.sources || []);
            setEvents(e.data.events || []);
            setMetrics(m.data || { modules: [], metrics: [] });
            setError(null);
        } catch (err) {
            // always text: a 422 `detail` is a list, and an object in the banner blanks the page
            setError(apiErrorText(err, t("error_generic")));
        } finally {
            setBusy(false);
        }
    }, [t]);

    useEffect(() => {
        load();
    }, [load]);

    // Writes a `system.test_emit` row to the event log (audited) — asked first.
    const testEmit = async () => {
        setConfirmEmit(false);
        if (emitting) return;
        setEmitting(true);
        try {
            await api.post("/system/events/test-emit");
            toast.success(t("sys_emit_success"));
            await load();
        } catch (e) {
            toast.error(apiErrorText(e, t("error_generic")));
        } finally {
            setEmitting(false);
        }
    };

    return (
        <div data-testid={S.ingestPage} className="space-y-5">
            <PageHeader
                icon={Activity}
                title={t("sys_ingest_title")}
                subtitle={t("sys_ingest_subtitle")}
                actions={
                    <Button
                        data-testid={S.refresh}
                        variant="soft"
                        size="sm"
                        onClick={load}
                        disabled={busy}
                        className="gap-2"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                        {t("period_apply")}
                    </Button>
                }
            />

            {error ? (
                <div className="rounded-xl border border-[color:var(--danger-soft)] bg-[color:var(--danger-soft-bg)] px-4 py-3 text-sm text-[color:var(--danger)]">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1 break-words">{error}</div>
                    </div>
                </div>
            ) : null}

            {/* ---- Ingest sources ---- */}
            <Card className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-bold text-[color:var(--ink)]">
                        {t("sys_sources_title")}
                    </CardTitle>
                    <p className="text-xs text-[color:var(--ink-muted)]">{t("sys_sources_desc")}</p>
                </CardHeader>
                <CardContent>
                    {sources === null ? (
                        <Skeleton className="h-32 w-full" />
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid={S.ingestTable}>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t("sys_col_source")}</TableHead>
                                        <TableHead>{t("sys_col_configured")}</TableHead>
                                        <TableHead>{t("sys_col_status")}</TableHead>
                                        <TableHead>{t("sys_col_last_run")}</TableHead>
                                        <TableHead className="text-end">{t("sys_col_runs")}</TableHead>
                                        <TableHead>{t("sys_col_error")}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sources.map((s) => (
                                        <TableRow key={s.source} data-testid={S.sourceRow(s.source)}>
                                            <TableCell>
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-[color:var(--ink)]">
                                                        {tn({ name_ar: s.name_ar, name_en: s.name_en })}
                                                    </span>
                                                    <code className="font-mono text-[10px] text-[color:var(--ink-faint)]">
                                                        {s.endpoint}
                                                    </code>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {s.configured ? (
                                                    <Badge variant="secondary" className="gap-1">
                                                        <CheckCircle2 className="h-3 w-3 text-[color:var(--success)]" />
                                                        {t("sys_configured")}
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="warning">{t("sys_not_configured")}</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell data-testid={S.sourceStatus(s.source)}>
                                                <IngestStatusBadge status={s.last_status} t={t} />
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                <bdi dir="ltr">{fmtWhen(s.last_run_at, lang)}</bdi>
                                            </TableCell>
                                            <TableCell className="text-end text-xs">
                                                <span className="text-[color:var(--success)] font-semibold">{s.ok_count}</span>
                                                {" / "}
                                                <span className="text-[color:var(--danger)] font-semibold">{s.fail_count}</span>
                                            </TableCell>
                                            <TableCell className="max-w-[280px]">
                                                <span
                                                    className="block truncate text-xs text-[color:var(--danger)]"
                                                    title={s.last_error || ""}
                                                >
                                                    {s.last_error || "—"}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ---- Alert-bus event log ---- */}
            <Card className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
                <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
                    <div className="min-w-0">
                        <CardTitle className="text-base font-bold text-[color:var(--ink)] flex items-center gap-2">
                            <Zap className="h-4 w-4 text-[color:var(--brand-orange)]" />
                            {t("sys_events_title")}
                        </CardTitle>
                        <p className="text-xs text-[color:var(--ink-muted)] mt-0.5">{t("sys_events_desc")}</p>
                    </div>
                    {isSuperAdmin ? (
                        <Button
                            data-testid={S.testEmitBtn}
                            variant="soft"
                            size="sm"
                            onClick={() => setConfirmEmit(true)}
                            disabled={emitting}
                            className="gap-2 shrink-0"
                        >
                            {emitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                            {t("sys_test_emit")}
                        </Button>
                    ) : null}
                </CardHeader>
                <CardContent>
                    {events === null ? (
                        <Skeleton className="h-24 w-full" />
                    ) : events.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-[color:var(--border-soft)] py-8 text-center text-sm text-[color:var(--ink-muted)]">
                            {t("sys_events_empty")}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid={S.eventsTable}>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t("sys_col_event")}</TableHead>
                                        <TableHead>{t("sys_col_source")}</TableHead>
                                        <TableHead>{t("sys_col_payload")}</TableHead>
                                        <TableHead>{t("sys_col_time")}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {events.map((ev) => (
                                        <TableRow key={ev.id} data-testid={S.eventRow(ev.id)} className={ev.voided ? "opacity-60" : undefined}>
                                            <TableCell>
                                                <Badge variant="brown" className="font-mono text-[11px]">
                                                    {ev.event_name}
                                                </Badge>
                                                {/* a voided alert is kept for history, but it is no longer live */}
                                                {ev.voided ? (
                                                    <Badge variant="secondary" className="ms-1 text-[10px]" title={ev.void_reason || ""}>
                                                        {t("sys_event_voided")}
                                                    </Badge>
                                                ) : null}
                                            </TableCell>
                                            <TableCell className="text-xs text-[color:var(--ink-muted)]">
                                                {ev.source || "—"}
                                            </TableCell>
                                            <TableCell className="max-w-[360px]">
                                                <code className="block truncate font-mono text-[10px] text-[color:var(--ink-muted)]">
                                                    {JSON.stringify(ev.payload || {})}
                                                </code>
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                <bdi dir="ltr">{fmtWhen(ev.created_at, lang)}</bdi>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {isSuperAdmin ? <CheckinRecomputeCard /> : null}

            {/* ---- Metric service catalog ---- */}
            <Card data-testid={S.metricsCard} className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-bold text-[color:var(--ink)] flex items-center gap-2">
                        <Gauge className="h-4 w-4 text-[color:var(--brand-sky)]" />
                        {t("sys_metrics_title")}
                    </CardTitle>
                    <p className="text-xs text-[color:var(--ink-muted)]">{t("sys_metrics_desc")}</p>
                </CardHeader>
                <CardContent>
                    {metrics === null ? (
                        <Skeleton className="h-16 w-full" />
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {(metrics.metrics || []).map((m) => (
                                <Badge key={`${m.module}-${m.key}`} variant="outline" className="gap-1.5">
                                    <span className="text-[color:var(--ink-faint)] text-[10px] uppercase">{m.module}</span>
                                    {tn({ name_ar: m.name_ar, name_en: m.name_en })}
                                </Badge>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={confirmEmit} onOpenChange={setConfirmEmit}>
                <AlertDialogContent data-testid={S.testEmitConfirm}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("sys_test_emit")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("sys_test_emit_confirm")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid={S.testEmitConfirmStart} onClick={testEmit}>
                            {t("sys_test_emit")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
